import React from "react";
import { Button, Skeleton, Table, TableProps } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { SorterResult } from "antd/es/table/interface";
import { IRankingSnapshotRow } from "modules/organization/models/interface";
import { mnDate } from "modules/shared/services";
import { dateFormat } from "config/commonConfig";
import {
  SnapshotSortColumn,
  SnapshotSortOrder,
  useRankingSnapshotStore,
} from "../store/rankingSnapshotStore";

interface RankingSnapshotTableViewProps {
  projectSlug: string;
  rankingProjectId: number;
  rows: IRankingSnapshotRow[];
  loading: boolean;
  skeletonRowCount: number;
  rankingType?: "template" | "point";
  onOpenRecord?: (record: IRankingSnapshotRow) => void;
}

const wireToAntdOrder = (
  order: SnapshotSortOrder | null,
): "ascend" | "descend" | null =>
  order === "asc" ? "ascend" : order === "desc" ? "descend" : null;

const antdToWireOrder = (
  order: "ascend" | "descend" | null | undefined,
): SnapshotSortOrder | null =>
  order === "ascend" ? "asc" : order === "descend" ? "desc" : null;

// Skeleton placeholder rows carry a negative record_id so they never collide with a real row's
// rowKey, and so column render functions below can detect them.
const SKELETON_ROW_ID_OFFSET = -1;
const isSkeletonRow = (record: IRankingSnapshotRow) =>
  record.record_id <= SKELETON_ROW_ID_OFFSET;

const skeletonCell = (width: number | string = "70%") => (
  <Skeleton.Input active size="small" style={{ width }} />
);

const RankingSnapshotTableView: React.FC<RankingSnapshotTableViewProps> = ({
  projectSlug,
  rankingProjectId,
  rows,
  loading,
  skeletonRowCount,
  rankingType,
  onOpenRecord,
}) => {
  const { sortColumn, sortOrder, setSort, latestRequestId } =
    useRankingSnapshotStore();

  void onOpenRecord;

  const defaultSortOrderOf = (column: string) => {
    if (sortColumn === column) {
      return wireToAntdOrder(sortOrder);
    }
    if (sortColumn === null) {
      if (column === "rank" && rankingType === "template") {
        return "ascend";
      }
      if (column === "points" && rankingType === "point") {
        return "descend";
      }
    }
    return null;
  };

  const skeletonRows: IRankingSnapshotRow[] = loading
    ? Array.from({ length: skeletonRowCount }, (_, index) => ({
        record_id: SKELETON_ROW_ID_OFFSET - index,
        record_name: "",
        record_type: "",
        effective_from: "",
        through_to: "",
        series_name: "",
        rank_name: null,
        rank_display_order: null,
        points: null,
      }))
    : [];

  const columns: ColumnsType<IRankingSnapshotRow> = [
    {
      title: "RECORD",
      dataIndex: "record_name",
      sorter: true,
      defaultSortOrder: defaultSortOrderOf("record"),
      render: (data, record) =>
        isSkeletonRow(record) ? (
          skeletonCell("60%")
        ) : (
          <span>
            {record.record_type ? `(${record.record_type}) ` : ""}
            {data}
          </span>
        ),
    },
    {
      title: "EFFECTIVE",
      dataIndex: "effective_from",
      align: "right",
      width: 110,
      sorter: true,
      defaultSortOrder: defaultSortOrderOf("effective"),
      render: (data, record) =>
        isSkeletonRow(record)
          ? skeletonCell(70)
          : data
            ? mnDate(data).format(dateFormat.default)
            : "",
    },
    {
      title: "THROUGH",
      dataIndex: "through_to",
      align: "right",
      width: 110,
      sorter: true,
      defaultSortOrder: defaultSortOrderOf("through"),
      render: (data, record) =>
        isSkeletonRow(record)
          ? skeletonCell(70)
          : data
            ? mnDate(data).format(dateFormat.default)
            : "",
    },
    {
      title: "SERIES",
      dataIndex: "series_name",
      width: 200,
      sorter: true,
      defaultSortOrder: defaultSortOrderOf("series"),
      render: (data, record) =>
        isSkeletonRow(record) ? skeletonCell("80%") : data,
    },
    ...(rankingType === "template"
      ? ([
          {
            title: "RANK",
            dataIndex: "rank_name",
            width: 150,
            sorter: true,
            defaultSortOrder: defaultSortOrderOf("rank"),
            render: (data: string, record: IRankingSnapshotRow) =>
              isSkeletonRow(record) ? skeletonCell(80) : data,
          },
        ] as ColumnsType<IRankingSnapshotRow>)
      : []),
    ...(rankingType === "point"
      ? ([
          {
            title: "POINTS",
            dataIndex: "points",
            width: 110,
            align: "right",
            sorter: true,
            defaultSortOrder: defaultSortOrderOf("points"),
            render: (data: number, record: IRankingSnapshotRow) =>
              isSkeletonRow(record) ? skeletonCell(60) : data,
          },
        ] as ColumnsType<IRankingSnapshotRow>)
      : []),
    {
      title: <span style={{ whiteSpace: "nowrap" }}>ACTION</span>,
      dataIndex: "record_id",
      width: 90,
      align: "center",
      render: (_data, record) =>
        isSkeletonRow(record) ? (
          <Skeleton.Avatar active size="small" shape="circle" />
        ) : (
          <span className="actionCellCenter">
            <Button
              className="iconOnlyBtn smallIcon"
              aria-label={`Open ${record.record_name}`}
              onClick={() => {}}
            >
              <ArrowRightOutlined role="img" aria-hidden />
            </Button>
          </span>
        ),
    },
  ];

  const columnKeyToSortColumn: Record<string, SnapshotSortColumn> = {
    record_name: "record",
    effective_from: "effective",
    through_to: "through",
    series_name: "series",
    rank_name: "rank",
    points: "points",
  };

  // Every sort change is now a server round-trip (MQRNK-164, 2026-08-10) — the table never
  // re-sorts `rows` itself, since `rows` is only ever the current page.
  const onChange: TableProps<IRankingSnapshotRow>["onChange"] = (
    _pagination,
    _filters,
    sorter,
  ) => {
    const { field, order } = sorter as SorterResult<IRankingSnapshotRow>;
    const column = field ? columnKeyToSortColumn[field.toString()] : null;
    setSort(
      projectSlug,
      rankingProjectId,
      column ?? null,
      antdToWireOrder(order),
    );
  };

  return (
    <Table
      key={`${rankingProjectId}-${rankingType ?? "unknown"}-${latestRequestId}`}
      columns={columns}
      dataSource={loading ? skeletonRows : rows}
      rowKey={(record) => record.record_id}
      pagination={false}
      onChange={onChange}
      showSorterTooltip={false}
      className="scrollableTable"
    />
  );
};

export default RankingSnapshotTableView;
